import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, Subject, firstValueFrom, startWith, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClientWrapper } from '../../../http-client-wrapper';
import { VehicleSnapshot } from '../models/vehicle-snapshot.models';

export type CachedSnapshots = { [vin: string]: VehicleSnapshot };
export type VehicleAlerts = {
  geofence: string[];
  low_battery_alert: boolean;
  overcharge_alert: boolean;
  high_speed_alert: boolean;
};
export type CachedAlerts = { [vin: string]: VehicleAlerts };
type LiveMicroserviceToken = { auth_token: string };

@Injectable({ providedIn: 'root' })
export class VehicleRealTimeService implements OnDestroy {
  /**
   * Websocket to Live Microservice
   */
  private _socket: WebSocket | undefined;
  private _socketFailed = false;

  /**
   * Cached snapshots dictionary
   */
  private _cachedSnapshots: CachedSnapshots = {};

  /**
   * Observable to listen to vehicle snapshots
   */
  private _snapshotsSubject = new Subject<CachedSnapshots>();
  public snapshots$: Observable<CachedSnapshots> = this._snapshotsSubject
    .asObservable()
    .pipe(startWith(this._cachedSnapshots));

  /**
   * Cached alerts dictionary
   */
  private _cachedAlerts: CachedAlerts = {};

  /**
   * Observable to listen to vehicle alerts
   */
  private _alertsSubject = new Subject<CachedAlerts>();
  public alerts$: Observable<CachedAlerts> = this._alertsSubject.asObservable();

  private _onlineStatusT?: number;

  private _disposed = false;

  constructor(private readonly http: HttpClientWrapper, private readonly store: Store) {}

  ngOnDestroy(): void {
    this.dispose();
  }

  start(): void {
    this.clear();

    void this._initializeSocket();

    // Update the cached snapshots online status every minute
    this._onlineStatusT = window.setInterval(() => {
      Object.values(this._cachedSnapshots).forEach((snapshot: VehicleSnapshot) => {
        if (snapshot.online) {
          snapshot.online = moment
            .parseZone(snapshot.timestamp + 'Z')
            .isAfter(moment().subtract(1, 'minute'));
        }
      });
      this._snapshotsSubject.next(this._cachedSnapshots);
    }, 60000);
  }

  clear(): void {
    if (!this._socket) return;

    console.log('Websocket cleared!');

    this._socket.onclose = () => {};
    this._socket.close();
    this._socket = undefined;
    this._socketFailed = false;

    if (this._onlineStatusT) window.clearInterval(this._onlineStatusT);
  }

  dispose(): void {
    this.clear();

    if (this._disposed) {
      console.warn(
        'VehicleRealTimeService is already disposed. Maybe you meant to call `.clear()` instead'
      );
      return;
    }

    this._snapshotsSubject.complete();
    this._alertsSubject.complete();
  }

  /**
   * Initializes the websocket connection to the live microservice
   * @returns {Promise<void>} - A promise that resolves when the websocket is connected
   * @author Juan Corral
   */
  private async _initializeSocket(): Promise<void> {
    this.clear();

    // Get the token to connect to the live microservice
    const liveToken = await firstValueFrom(
      this.http.post<LiveMicroserviceToken>('client/live_registration/', {})
    );

    return new Promise(resolve => {
      // Create the websocket connection
      this._socket = new WebSocket(
        `${environment.liveMicroserviceUrl}?token=${liveToken.auth_token}`
      );

      // Set the observables to listen to the websocket messages
      this._socket.onmessage = (event: MessageEvent) => {
        if (!event.data) return;
        const data = JSON.parse(event.data);

        switch (data.action) {
          case 'broadcaster':
            if (data.content?.vehicle) {
              this._updateCachedSnapshot(data.content.vehicle);
              if (data.content?.alerts)
                this._updateCachedAlert(data.content.vehicle.vin, data.content.alerts);
            }
            break;
          // deprecated, prefer a single backend API pull
          case 'get-last-vehicle-states':
            if (data.content) this._updateCachedSnapshots(data.content);
            break;
          default:
            break;
        }
      };

      // Reconnect the websocket if it closes
      this._socket.onclose = msg => {
        console.log('Socket closed', msg);

        if (!this._socketFailed) {
          console.log('Socket closed. Trying to reconnect');
          this.clear();
          void this._initializeSocket();
        } else {
          console.log('Socket closed with error. Will not try to reconnect');
        }
      };

      this._socket.onerror = err => {
        console.log('Socket errored', err);
        this._socketFailed = true;
      };

      this._socket.onopen = () => {
        console.log('Websocket connection established');
        resolve();
      };
    });
  }

  /**
   * Triggers the websocket to get the cached snapshots for the given vins.
   * @param {string[]} vins - The list of vins to get the cached snapshots from.
   * @author Juan Corral
   */
  getCachedSnapshots(): Observable<CachedSnapshots> {
    return this.http
      .get<CachedSnapshots>('vehicle/last_states/')
      .pipe(tap(snapshots => this._updateCachedSnapshots(snapshots)));
  }

  getSnapshot(vin: string): VehicleSnapshot | undefined {
    return this._cachedSnapshots[vin];
  }

  /**
   * Updates the cached snapshots with the new snapshot.
   * @param {VehicleSnapshot} snapshot - The new snapshot to update the cached snapshots with.
   * @param {boolean} emit - Whether to emit the new snapshot to the observable or not.
   * @author Juan Corral
   */
  private _updateCachedSnapshot(snapshot: VehicleSnapshot, emit = true): void {
    snapshot.online = moment
      .parseZone(snapshot.timestamp + 'Z')
      .isAfter(moment().subtract(1, 'minute'));
    this._cachedSnapshots[snapshot.vin] = snapshot;
    if (emit) this._snapshotsSubject.next(this._cachedSnapshots);
  }

  /**
   * Updates the cached snapshots with the new snapshots.
   * @param {CachedSnapshots} snapshots - The new snapshots to update the cached snapshots with.
   * @author Juan Corral
   */
  private _updateCachedSnapshots(snapshots: CachedSnapshots): void {
    for (const vin in snapshots) this._updateCachedSnapshot(snapshots[vin], false);
    this._snapshotsSubject.next(this._cachedSnapshots);
  }

  /**
   * Updates the cached alerts with the new alert.
   * @param {VehicleAlerts} alert - The new alert to update the cached alerts with.
   * @author Juan Corral
   */
  private _updateCachedAlert(vin: string, alert: VehicleAlerts): void {
    this._cachedAlerts[vin] = alert;
    this._alertsSubject.next(this._cachedAlerts);
  }
}
